import { useTranslation } from "react-i18next";
import { Divider, Form, Icon, Loader, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";

import type { DropdownItemProps } from "components";
import type { ChecklistTemplate } from "models";

type TemplateFormModalProps = {
  isEdit: boolean;
  isPending: boolean;
  reportTemplateOptions: DropdownItemProps[];
  isFetchingTemplate?: boolean;
  onClose: () => void;
};

const FormHelper = createFormHelper<ChecklistTemplate>();

export const TemplateModalForm = ({ isEdit, isPending, reportTemplateOptions, isFetchingTemplate, onClose }: TemplateFormModalProps) => {
  const { t } = useTranslation();
  const { watch, formState } = FormHelper.useFormContext();

  const name = watch("name");

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container ">
          <div className="header-title">
            {isEdit ? t("v8_edit_checklist_template", "Edit Checklist Template") : t("v8_create_checklist_template", "Create Checklist Template")}
          </div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        {isFetchingTemplate ? (
          <Loader active inline="centered" className="Loader" />
        ) : (
          <>
            <Form.Group widths="equal">
              <Form.Field required error={formState.isSubmitted && (!name || name.trim().length === 0)}>
                <label>{t("v8_name", "Name")}</label>
                <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_name", "Name") }} />
              </Form.Field>
              <Form.Field>
                <label>{t("v8_description", "Description")}</label>
                <FormHelper.Field.Input name="description" inputProps={{ placeholder: t("v8_description", "Description") }} />
              </Form.Field>
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field>
                <label>{t("v8_pdf_template", "PDF Template")}</label>
                <FormHelper.Field.Dropdown
                  name="pdf_layout_id"
                  options={reportTemplateOptions}
                  dropdownProps={{ placeholder: t("v8_select_template", "Select template"), selectOnBlur: false }}
                />
              </Form.Field>
            </Form.Group>

            <div className="settings-section">
              <div className="section-title">
                <Icon className="eye dropper" />
                {t("v8_color_settings", "Color settings")}
              </div>
              <div className="color-fields-horizontal">
                <div className="color-field">
                  <FormHelper.Field.ColorPicker name="primary_color" />
                  <label>{t("v8_primary_color", "Primary color")}</label>
                </div>
                <div className="color-field">
                  <FormHelper.Field.ColorPicker name="text_color" />
                  <label>{t("v8_text_color", "Text color")}</label>
                </div>
                <div className="color-field">
                  <FormHelper.Field.ColorPicker name="icon_color" />
                  <label>{t("v8_icon_color", "Icon color")}</label>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <div className="checkbox-columns">
                <div className="checkbox-column">
                  <div className="section-title">
                    <Icon className="settings" />
                    {t("v8_global_settings", "Global settings")}
                  </div>
                  <FormHelper.Field.Checkbox name="multi_use_enabled" label={t("v8_multi_use_enabled", "Multi-use enabled")} />
                  <FormHelper.Field.Checkbox name="can_skip_km_and_date" label={t("v8_allow_to_skip_mileage_and_next_date", "Allow to skip mileage and next date")} />
                  <FormHelper.Field.Checkbox name="skip_update_appointment_status" label={t("v8_skip_updating_appointment_status", "Skip updating appointment status")} />
                  <FormHelper.Field.Checkbox name="hide_timestamp" label={t("v8_do_not_show_timestamps", "Do not show timestamps")} />
                  <FormHelper.Field.Checkbox name="default_only" label={t("v8_default_only", "Default only")} />
                  <FormHelper.Field.Checkbox name="add_purpose" label={t("v8_add_purpose", "Add purpose")} />
                </div>
                <div className="checkbox-column">
                  <div className="section-title">
                    <Icon className="list" />
                    {t("v8_question_settings", "Question settings")}
                  </div>
                  <FormHelper.Field.Checkbox name="snooze_enabled" label={t("v8_snooze_enabled", "Snooze enabled")} />
                  <FormHelper.Field.Checkbox name="pin_enabled" label={t("v8_pin_enabled", "Pin enabled")} />
                  <FormHelper.Field.Checkbox name="show_mechanic_name" label={t("v8_show_mechanic_name", "Show mechanic name")} />
                  <FormHelper.Field.Checkbox name="cc_default_selected" label={t("v8_preselected_on_customer_communication", "Pre-selected on customer communication")} />
                  <FormHelper.Field.Checkbox
                    name="cc_default_selected_nvt"
                    label={t("v8_nvt_preselected_in_customer_communication", "NVT pre-selected in customer communication")}
                  />
                </div>
              </div>
            </div>

            <div className="settings-section">
              <div className="checkbox-columns">
                <div className="checkbox-column">
                  <div className="section-title">
                    <Icon className="clipboard list" />
                    {t("v8_report_settings", "Report settings")}
                  </div>
                  <FormHelper.Field.Checkbox name="include_in_reports" label={t("v8_export_to_reporting", "Export to reporting")} />
                  <FormHelper.Field.Checkbox name="total_pdf_enabled" label={t("v8_total_pdf_enabled", "Total PDF enabled")} />
                </div>
                <div className="checkbox-column">
                  <div className="section-title">
                    <Icon className="clipboard check" />
                    {t("v8_status_settings", "Status settings")}
                  </div>
                  <FormHelper.Field.Checkbox name="customer_ok_enabled" label={t("v8_customer_ok_enabled", "Customer OK enabled")} />
                  <FormHelper.Field.Checkbox name="ready_enabled" label={t("v8_ready_enabled", "Ready enabled")} />
                </div>
              </div>
            </div>

            <div className="settings-section">
              <div className="checkbox-columns">
                <div className="checkbox-column">
                  <div className="section-title">
                    <Icon className="euro sign" />
                    {t("v8_price_settings", "Price settings")}
                  </div>
                  <FormHelper.Field.Checkbox name="price_enabled" label={t("v8_price_enabled", "Price enabled")} />
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="action-buttons-container">
          <div className="right-buttons">
            <Form.Button type="button" className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
